"use client";

import { c as _c } from "react/compiler-runtime";
import { useTranslation } from "@/app/i18n/client";
import { useForm } from "react-hook-form";
import { makeZodI18nMap } from "@daubit/zod-i18n-map";
import { z } from "zod/v4";
import { InputFormField } from "@/app/[lng]/_components/Forms/input-form-field";
import Link from "next/link";
import { zodResolver } from "@hookform/resolvers/zod";
import { LocaleName } from "@/app/i18n/settings";
import { sendGTMEvent } from "@/components/Tracking/TagManagerWrapper";
import { AnalyticsActions, AnalyticsCategories, AnalyticsEventType, AnalyticsLabels } from "@/constants/analytics";
import { usePathname, useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import { Form, FormField, FormItem } from "@/components/ui/form";
import LoadingSpinnerBlock from "@/components/LoadingSpinner/LoadingSpinnerBlock";
import { LoginData, zodLoginData } from "@/types/zod/login";
import { LoginUser } from "./actions";
import { MdLock, MdPerson } from "react-icons/md";
import { Button } from "@/components/ui/button";
import { getLocalizedRoute } from "@/utils/localized-route";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Route } from "next";
export function LoginForm(t0) {
  const $ = _c(29);
  const {
    redirect,
    reload,
    closeModal
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["common", "zod", "zodCustomErrors"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t,
    i18n
  } = useTranslation(t1);
  const [success, setSuccess] = useState(false);
  const router = useRouter();
  const lng = i18n.language as LocaleName;
  z.config(makeZodI18nMap({
    t,
    ns: ["zod", "zodCustomErrors"]
  }));
  const pathname = usePathname();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      mode: "onChange",
      reValidateMode: "onChange",
      resolver: zodResolver(zodLoginData),
      defaultValues: {
        username: "",
        password: ""
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const formMethods = useForm(t2);
  let t3;
  if ($[2] !== closeModal || $[3] !== lng || $[4] !== pathname || $[5] !== redirect || $[6] !== reload || $[7] !== router) {
    t3 = {
      mutationFn: async values => {
        const redirectTo = pathname !== getLocalizedRoute(lng, "login") && pathname ? getLocalizedRoute(lng, "login") : getLocalizedRoute(lng, "userpanel/companyprofile");
        const result = await LoginUser(values);
        if (result.successful) {
          setSuccess(true);
          sendGTMEvent(AnalyticsEventType.Event, AnalyticsCategories.login, AnalyticsLabels.user, AnalyticsActions.loggedIn);
          reload && router.refresh();
          redirect && router.push(redirectTo);
          closeModal && closeModal();
          return;
        }
        if (!result.successful && result.errordesc.match(/confirmed/g)?.length) {
          sendGTMEvent(AnalyticsEventType.Event, AnalyticsCategories.login, AnalyticsLabels.user, AnalyticsActions.userNotActivated);
          throw new Error("ERRORMESSAGES.LOGINERROR.USERNOTACTIVATED");
        } else {
          sendGTMEvent(AnalyticsEventType.Event, AnalyticsCategories.login, AnalyticsLabels.user, AnalyticsActions.invalidLoginData);
          throw new Error("ERRORMESSAGES.LOGINERROR.LOGINERROR");
        }
      }
    };
    $[2] = closeModal;
    $[3] = lng;
    $[4] = pathname;
    $[5] = redirect;
    $[6] = reload;
    $[7] = router;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    mutateAsync: loginMutation,
    isPending,
    error
  } = useMutation(t3);
  let t4;
  if ($[9] !== loginMutation) {
    t4 = async function onSubmit(values_0) {
      sendGTMEvent(AnalyticsEventType.Event, AnalyticsCategories.login, AnalyticsLabels.button, AnalyticsActions.loginClick);
      ;
      try {
        await loginMutation(values_0);
      } catch (t5) {
        const e = t5;
        if (e instanceof Error) {
          bb43: switch (e.message) {
            case "ERRORMESSAGES.LOGINERROR.LOGINERROR":
              {
                console.warn("Login failed");
                break bb43;
              }
            case "ERRORMESSAGES.LOGINERROR.USERNOTACTIVATED":
              {
                console.warn("User not activated");
                break bb43;
              }
            default:
              {
                throw e;
              }
          }
        } else {
          throw e;
        }
      }
    };
    $[9] = loginMutation;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const onSubmit = t4;
  const {
    handleSubmit,
    formState: t5
  } = formMethods;
  const {
    errors,
    isValid
  } = t5;
  let t6;
  if ($[11] !== isPending) {
    t6 = isPending && <LoadingSpinnerBlock small={true} />;
    $[11] = isPending;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== error || $[14] !== errors || $[15] !== formMethods.control || $[16] !== handleSubmit || $[17] !== isPending || $[18] !== isValid || $[19] !== lng || $[20] !== onSubmit || $[21] !== redirect || $[22] !== success || $[23] !== t) {
    t7 = success && redirect ? <div className="flex flex-row justify-center gap-1"><div className="text-lg">{t("SIGNUP.LOGIN.REDIRECTMESSAGE.TEXT")}</div><div className="animate-bounce text-lg [animation-delay:-0.3s]">.</div><div className="animate-bounce text-lg [animation-delay:-0.15s]">.</div><div className="animate-bounce text-lg">.</div></div> : <form className={cn("w-72", isPending && "hidden")} onSubmit={handleSubmit(onSubmit)}><div className="form-title mb-2">{t("SIGNUP.LOGIN.INFOTEXT.TEXT")}</div><div className="table-row-mb"><FormField control={formMethods.control} name="username" render={t8 => {
          const {
            field
          } = t8;
          return <FormItem className="w-full"><InputFormField {...field} label={t("SIGNUP.MAIN.USERNAME.TEXT") ?? "Username"} icon={<MdPerson className="mb-2 h-5 w-5" />} autoComplete="username" error={errors.username?.message} /></FormItem>;
        }} /></div><div className="table-row-mb"><FormField control={formMethods.control} name="password" render={t9 => {
          const {
            field: field_0
          } = t9;
          return <InputFormField {...field_0} label={t("SIGNUP.MAIN.PASSWORD.TEXT") ?? "Password"} icon={<MdLock className="mb-2 h-5 w-5" />} autoComplete="password" type="password" error={errors.password?.message} />;
        }} /></div><div className="table-row-mb"><div className="form-group"><span className="form-error text-green-500">{error && t(error?.message as `ERRORMESSAGES.LOGINERROR.${"INVALIDUSERNAME" | "USERNOTACTIVATED" | "LOGINERROR"}`)}</span></div></div><div className="table-row-mb"><Button variant="submit" disabled={!isValid || isPending} className="w-full">{t("MAIN.SIGNUP.TEXT")}</Button></div><div className="mt-2 text-center"><Link className="text-xs text-secondary" href={getLocalizedRoute(lng, "forgotpassword")}>{t("SIGNUP.MAIN.RESETPASSWORD.TEXT")}</Link></div></form>;
    $[13] = error;
    $[14] = errors;
    $[15] = formMethods.control;
    $[16] = handleSubmit;
    $[17] = isPending;
    $[18] = isValid;
    $[19] = lng;
    $[20] = onSubmit;
    $[21] = redirect;
    $[22] = success;
    $[23] = t;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== formMethods || $[26] !== t6 || $[27] !== t7) {
    t8 = <Form {...formMethods}>{t6}{t7}</Form>;
    $[25] = formMethods;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  return t8;
}